import { useTenant } from '$features/auth/hooks/use-tenant'
import { TabLayout } from '$features/settings/layouts/tab'
import { Alert, Breadcrumb, Button, Divider, Text, Tooltip } from '$components'
import { EditIcon, PlusIcon, SettingsIcon } from '$components'
import { ElementIntent } from '$components'
import { useQuery } from '@tanstack/react-query'
import { locationIndexOptions } from '$api-client/@tanstack/react-query.gen'
import { AnimatedOutlet } from '$components'
import { useNavigate } from 'react-router'
import { SettingsRoutePath } from '$features/settings/routes/protected'
import { LocationCard } from '$features/settings/components/locations/location-card'
import { SUBSCRIPTION_EXPIRED_MESSAGE } from '$constants/subscription'
import { DisplayLabel } from '$components'
import { usePageTitle } from '$components'

export function GeneralTab() {
  usePageTitle('Settings - General')
  const { name, contactEmail, contactPhone, hasSubscriptionExpired } = useTenant()
  const navigate = useNavigate()

  const { data: locations } = useQuery({
    ...locationIndexOptions(),
  })

  const handleAddLocation = () => {
    navigate(SettingsRoutePath.SettingsLocationCreate)
  }

  const handleEditBusinessDetails = () => {
    navigate(SettingsRoutePath.SettingsBusinessNameEdit)
  }

  return (
    <TabLayout>
      <Breadcrumb
        items={[
          {
            label: 'Settings',
            icon: SettingsIcon,
            path: SettingsRoutePath.Settings,
          },
          {
            label: 'General',
            path: SettingsRoutePath.SettingsGeneral,
          },
        ]}
      />

      <div>
        <Text
          type="h2"
          weight="medium"
        >
          General Settings
        </Text>

        <Text
          type="s"
          className="text-gray-500 mt-1"
        >
          Configure your business details, locations and operating hours.
        </Text>
      </div>

      <div className="space-y-6">
        <div className="flex items-center gap-4">
          <div className="flex-1">
            <DisplayLabel>Business Name</DisplayLabel>
            <Text type="s">
              {name}
            </Text>
          </div>

          <Tooltip
            content={hasSubscriptionExpired ? SUBSCRIPTION_EXPIRED_MESSAGE : undefined}
            disabled={!hasSubscriptionExpired}
          >
            <Button
              variant="secondary"
              size="sm"
              onClick={handleEditBusinessDetails}
              icon={EditIcon}
              disabled={hasSubscriptionExpired}
            >
              Edit
            </Button>
          </Tooltip>
        </div>

        <div className="flex items-center gap-4">
          <div className="flex-1">
            <DisplayLabel>Contact Email</DisplayLabel>
            <Text type="s">
              {contactEmail || 'Not set'}
            </Text>
          </div>
        </div>

        <div className="flex items-center gap-4">
          <div className="flex-1">
            <DisplayLabel>Contact Phone</DisplayLabel>
            <Text type="s">
              {contactPhone || 'Not set'}
            </Text>
          </div>
        </div>

        <Alert intent={ElementIntent.Info}>
          <Alert.Heading>
            Note
          </Alert.Heading>
          <Alert.Body>
            This information is used in transactional emails sent to your customers, such as appointment confirmations and reminders.
          </Alert.Body>
        </Alert>
      </div>

      <Divider />

      <div className="flex items-center justify-between">
        <div>
          <Text
            type="h2"
            weight="medium"
          >
            Locations
          </Text>

          <Text
            type="s"
            className="text-gray-500 mt-1"
          >
            Manage your organisation&apos;s locations and business hours
          </Text>
        </div>

        <Tooltip
          disabled={!hasSubscriptionExpired && locations?.data.length === 0}
          content={hasSubscriptionExpired ? SUBSCRIPTION_EXPIRED_MESSAGE : 'You can only have one location at the time.'}
        >
          <Button
            variant="primary"
            icon={PlusIcon}
            onClick={handleAddLocation}
            disabled={hasSubscriptionExpired || (locations?.data.length ?? 0) > 0}
          >
            Add Location
          </Button>
        </Tooltip>
      </div>

      <div className="flex flex-col gap-4">
        {locations?.data.map((location, key) => (
          <LocationCard
            key={key}
            location={location}
          />
        ))}
      </div>

      <AnimatedOutlet />
    </TabLayout>
  )
}
